import React from 'react';
import {
    Page,
    Card,
    Layout,
    FormLayout,
    Select,
    ChoiceList,
} from '@shopify/polaris';
import { Helmet } from 'react-helmet';
import { apiFetch as fetch } from '../common/Helpers';
import { AppContext } from '../common/AppContext';
import { IanaTimezones } from '../common/IanaTimeZones';

var ianaTimezoneOptions = [
    { label: 'Americas', value: 'americas' },
];

const firstDayOptions = [
    { label: 'Monday', value: 'Monday' },
    { label: 'Tuesday', value: 'Tuesday' },
    { label: 'Wednesday', value: 'Wednesday' },
    { label: 'Thursday', value: 'Thursday' },
    { label: 'Friday', value: 'Friday' },
    { label: 'Saturday', value: 'Saturday' },
    { label: 'Sunday', value: 'Sunday' },
];

const calendarWeekRuleOptions = [
    { label: '[First day] Indicates that the first week of the year starts on the first day of the year and ends before the following designated first day of the week.', value: 'FirstDay' },
    { label: '[First four day week] Indicates that the first week of the year is the first week with four or more days before the designated first day of the week.', value: 'FirstFourDayWeek' },
    { label: '[First full week] Indicates that the first week of the year begins on the first occurrence of the designated first day of the week on or after the first day of the year.', value: 'FirstFullWeek' },
];

class SettingsPage extends React.Component {

    state = {
        ianaTimezone: null,
        firstDayOfWeek: null,
        calendarWeekRule: null,
        validationRulesProductStatuses: [],
        dataAccessPermissions: []
    };

    constructor(props, context) {
        super(props, context)

        this.state.dataAccessPermissions.push("products");

        if (context.hasCustomerPermissions) {
            this.state.dataAccessPermissions.push("customers");
        }

        if (context.hasOrderPermissions) {
            this.state.dataAccessPermissions.push("orders");
        }

        if (context.hasFulfillmentOrderPermissions) {
            this.state.dataAccessPermissions.push("fulfillmentOrders");
        }
    }

    render() {

        return (
            <div>
                <Helmet>
                    <title>Tag Genie - Settings</title>
                </Helmet>
                <Page title="Settings"
                    subtitle="View or change application settings">
                    <Layout>
                        <Layout.AnnotatedSection
                            id="timezoneSettings"
                            title="Timezone settings"
                            description="Use the settings to choose the timezone that you are operating from so that scheduler tag operations and time/calendar-dependent operations are executed correctly."
                        >
                            <Card sectioned primaryFooterAction={{ content: "Update", onAction: this.onUpdateTimezoneSettings }}>
                                <FormLayout>
                                    <Select
                                        label="Store timezone"
                                        onChange={(value) => { this.setState({ ianaTimezone: value }) }}
                                        autoComplete="off"
                                        value={this.state.ianaTimezone}
                                        options={ianaTimezoneOptions}
                                        helpText={
                                            <span>Specifies the timeone your store operates from.</span>
                                        }
                                    />
                                    <Select
                                        label="First day of week"
                                        onChange={(value) => { this.setState({ firstDayOfWeek: value }) }}
                                        autoComplete="off"
                                        value={this.state.firstDayOfWeek}
                                        options={firstDayOptions}
                                        helpText={
                                            <span>Specifies the first day of the week.</span>
                                        }
                                    />
                                    <Select
                                        label="First week of year rule"
                                        onChange={(value) => { this.setState({ calendarWeekRule: value }) }}
                                        autoComplete="off"
                                        value={this.state.calendarWeekRule}
                                        options={calendarWeekRuleOptions}
                                        helpText={
                                            <span>Defines different rules for determining the first week of the year.</span>
                                        }
                                    />
                                </FormLayout>
                            </Card>
                        </Layout.AnnotatedSection>
                        <Layout.AnnotatedSection
                            id="dataPermissionSettings"
                            title="Data Permission Settings"
                            description="Configure permissions to access data"
                        >
                            <Card sectioned primaryFooterAction={{ content: "Update", onAction: this.updatePermissionSettings }}>
                                <FormLayout>
                                    <ChoiceList
                                        allowMultiple
                                        title="Allow Tag Genie to access the following data:"
                                        choices={[
                                            {
                                                disabled: true,
                                                label: 'Products',
                                                value: 'products',
                                                helpText: 'Access product data. Needed to work with product tags.'
                                            },
                                            {
                                                label: 'Orders',
                                                value: 'orders',
                                                helpText: 'Access customer data. Needed to work with order tags.'
                                            },
                                            {
                                                label: 'Customers',
                                                value: 'customers',
                                                helpText: 'Access customer data. Needed to work with customer tags.'
                                            },
                                            {
                                                label: 'Fulfillment Orders',
                                                value: 'fulfillmentOrders',
                                                helpText: 'Access fulfillment order data. Needed to tag orders by fulfillment location.'
                                            }
                                        ]}
                                        selected={this.state.dataAccessPermissions}
                                        onChange={this.onDataAccessPermissionsChange}
                                    />
                                </FormLayout>
                            </Card>
                        </Layout.AnnotatedSection>
                        <Layout.AnnotatedSection
                            id="validationRulesSettings"
                            title="Validation rules settings"
                            description="Configure global settings for Validation Rules."
                        >
                            <Card sectioned primaryFooterAction={{ content: "Update", onAction: this.onUpdateTimezoneSettings }}>
                                <FormLayout>
                                    <ChoiceList
                                        allowMultiple
                                        title="Validate products in the following states:"
                                        choices={[
                                            {
                                                label: 'Active',
                                                value: 'ACTIVE',
                                                helpText: 'Products in the Active state will be validated'
                                            },
                                            {
                                                label: 'Archived',
                                                value: 'ARCHIVED',
                                                helpText: 'Products in the Archived state will be validated'
                                            },
                                            {
                                                label: 'Draft',
                                                value: 'DRAFT',
                                                helpText: 'Products in the Draft state will be validated'
                                            },
                                        ]}
                                        selected={this.state.validationRulesProductStatuses}
                                        onChange={this.onIgnoredProducStatusesChange}
                                    />
                                </FormLayout>
                            </Card>
                        </Layout.AnnotatedSection>
                    </Layout>
                </Page>
            </div >
        );
    }

    onUpdateTimezoneSettings = () => {
        fetch(`api/storeConnections/settings`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ianaTimezone: this.state.ianaTimezone,
                firstDayOfWeek: this.state.firstDayOfWeek,
                calendarWeekRule: this.state.calendarWeekRule,
                validationRulesProductStatuses: this.state.validationRulesProductStatuses
            })
        });
    }

    updatePermissionSettings = () => {
        
        const appContext = this.context;

        appContext.requestChangePermissions(
            this.state.dataAccessPermissions.includes("products"),
            this.state.dataAccessPermissions.includes("customers"),
            this.state.dataAccessPermissions.includes("orders"),
            this.state.dataAccessPermissions.includes("fulfillmentOrders"),
        );
    }

    getSettings = () => {
        fetch(`api/storeConnections/settings`)
            .then(response => response.json())
            .then((data) => {
                this.setState({
                    ianaTimezone: data.ianaTimezone,
                    firstDayOfWeek: data.firstDayOfWeek,
                    calendarWeekRule: data.calendarWeekRule,
                    validationRulesProductStatuses: data.validationRulesProductStatuses
                })
            });
    }

    onIgnoredProducStatusesChange = (value) => {
        this.setState({
            validationRulesProductStatuses: value
        })
    }

    onDataAccessPermissionsChange = (value) => {
        this.setState({
            dataAccessPermissions: value
        })
    }

    generateTimezoneOptions = () => {

        ianaTimezoneOptions = IanaTimezones.zones
            .map(_ => {

                if (_.location == null) {
                    return {
                        value: _.id,
                        label: _.id,
                    }
                }

                return {
                    value: _.id,
                    label: `${_.location.countryCode} - ${_.location.countryName} - ${_.location.comment}`,
                }
            });
    }

    componentDidMount() {
        this.getSettings();
        this.generateTimezoneOptions();
    }
}

SettingsPage.contextType = AppContext;

export default SettingsPage;